import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight, Sprout, Trophy, Tv } from "lucide-react";
import { api } from "@/lib/api";
import { HERO } from "@/constants/testIds";
import Workstreams from "@/components/Workstreams";

const HERO_VIDEO =
  "https://customer-assets.emergentagent.com/job_41fcd887-8ec6-40f9-b180-4549d29a6fe7/artifacts/f0d8gvr9_WhatsApp%20Video%202026-05-29%20at%2011.48.39%20PM.mp4";

const SPONSOR_LOGOS = [
  {
    name: "Niger State Government",
    logo: "https://customer-assets.emergentagent.com/job_farm-show-hub/artifacts/lzz96qxh_image.png",
  },
  {
    name: "Niger State Ministry of Agriculture",
    logo: "https://customer-assets.emergentagent.com/job_farm-show-hub/artifacts/4qsld5lh_WhatsApp%20Image%202026-05-30%20at%2012.30.08%20AM.jpeg",
  },
  {
    name: "Sydani Group",
    logo: "https://customer-assets.emergentagent.com/job_farm-show-hub/artifacts/n568u5gk_image.png",
  },
  {
    name: "Olohi Integrated Farms Limited",
    logo: "https://customer-assets.emergentagent.com/job_farm-show-hub/artifacts/frtrqyxa_WhatsApp%20Image%202026-05-30%20at%2012.27.32%20AM.jpeg",
  },
  {
    name: "National Orientation Agency (NOA)",
    logo: "https://customer-assets.emergentagent.com/job_farm-show-hub/artifacts/8r20isjs_image.png",
  },
];

export default function Home() {
  const [stats, setStats] = useState(null);
  const [contestants, setContestants] = useState([]);
  const videoRef = useRef(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/contestants").then((r) => setContestants(r.data.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <div className="bg-[#0A0A0A] text-white">
      {/* HERO */}
      <section className="relative min-h-[100vh] overflow-hidden">
        <video
          ref={videoRef}
          data-testid={HERO.video}
          className="absolute inset-0 w-full h-full object-cover"
          src={HERO_VIDEO}
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/30" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-20 min-h-[100vh] flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-3 mb-6"
          >
            <span className="h-px w-12 bg-[#F26A1F]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#F26A1F] font-bold">
              Agrovest Reality TV Show · Season 1
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="font-black tracking-tighter leading-[0.95] text-5xl sm:text-7xl lg:text-[8.5rem] max-w-5xl"
            style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
          >
            Show me
            <br />
            <span className="text-[#F26A1F]">the money.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 max-w-2xl text-lg text-white/75 leading-relaxed"
          >
            Nigeria is looking for the next Agro Millionaire. One platform.
            One opportunity. One champion. Represent your state and compete for
            <span className="text-[#2BA84A] font-bold"> ₦20 Million </span>
            on Global TV.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/apply"
              data-testid={HERO.primaryCta}
              className="group h-14 px-8 inline-flex items-center gap-3 rounded-sm bg-[#F26A1F] hover:bg-[#D95A15] text-white text-sm uppercase tracking-[0.18em] font-bold transition-all shadow-[0_0_24px_0px_rgba(242,106,31,0.4)] hover:shadow-[0_0_40px_4px_rgba(242,106,31,0.5)]"
            >
              Register for Auditions, Free
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="#video"
              data-testid={HERO.secondaryCta}
              className="h-14 px-8 inline-flex items-center gap-3 rounded-sm border border-[#1E8E3E]/70 hover:border-[#2BA84A] text-white text-sm uppercase tracking-[0.18em] font-bold transition-colors"
            >
              <Play size={16} /> Watch Season 1 Trailer
            </a>
          </motion.div>

          {/* Vertical badge top-left of hero */}
          <div className="absolute top-32 right-6 lg:right-12 hidden lg:flex flex-col items-end gap-3 text-right">
            <span className="text-xs uppercase tracking-[0.3em] text-white/40">
              37 States · 37 Contestants
            </span>
            <span className="text-xs uppercase tracking-[0.3em] text-white/40">
              60 Days · 13 Episodes
            </span>
          </div>
        </div>

        <div className="absolute bottom-6 left-0 right-0 z-10 flex justify-center">
          <span className="text-xs uppercase tracking-[0.4em] text-white/40 animate-pulse">
            scroll to discover
          </span>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-y border-white/10 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
          {[
            ["₦20M", "Grand Prize"],
            ["37", "Contestants"],
            ["37", "States"],
            ["60", "Days Live"],
            ["13", "Episodes"],
          ].map(([n, l], i) => (
            <motion.div
              key={l}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="text-center md:text-left"
            >
              <div className="text-4xl md:text-5xl font-black text-[#F26A1F] tracking-tighter">
                {n}
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 mt-1">
                {l}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHAT IS ARTS */}
      <section id="video" className="max-w-7xl mx-auto px-6 lg:px-12 py-28 grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5">
          <div className="text-xs uppercase tracking-[0.3em] text-[#F26A1F] mb-4">
            What is ARTS
          </div>
          <h2
            className="text-4xl md:text-5xl font-bold tracking-tighter leading-[1.05]"
            style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
          >
            Not just a TV show.<br />
            A national
            <span className="text-[#2BA84A]"> agricultural </span>
            revolution.
          </h2>
        </div>
        <div className="md:col-span-7">
          <p className="text-base text-white/70 leading-relaxed">
            Agrovest Reality TV Show is Nigeria's first and most ambitious
            agri-entertainment platform. Each season, 37 passionate young
            Nigerians, one from every state and the FCT, enter the Agrovest
            Farm Hub for 60 intense days of farming, innovation, agribusiness
            competition, and personal transformation.
          </p>
          <p className="mt-5 text-base text-white/70 leading-relaxed">
            Broadcast on international, Global TV and digital
            platforms, ARTS is where agriculture meets ambition, and where
            Nigeria's next generation of agripreneurs are born.
          </p>
          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] font-bold text-[#F26A1F] hover:text-white transition-colors"
          >
            Read the full story <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS preview */}
      <section className="bg-[#0c0c0c] border-y border-white/5 py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#F26A1F] mb-3">
                The Journey
              </div>
              <h2
                className="text-4xl md:text-5xl font-bold tracking-tighter"
                style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
              >
                How it works.
              </h2>
            </div>
            <Link
              to="/how-it-works"
              className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 hover:text-[#F26A1F]"
            >
              See full 5-step process →
            </Link>
          </div>
          <div className="grid md:grid-cols-12 gap-4">
            {[
              { n: "01", t: "Register", d: "Free signup. Upload your 2-min audition video.", span: "md:col-span-5", icon: Sprout },
              { n: "02", t: "Audition", d: "State-level physical or online auditions.", span: "md:col-span-7", icon: Tv },
              { n: "03", t: "Selected", d: "37 champions chosen, one per state + FCT.", span: "md:col-span-4" },
              { n: "04", t: "60 Days Live", d: "Farm Hub. 13 episodes. Real challenges.", span: "md:col-span-4" },
              { n: "05", t: "Win & Join Alumni", d: "₦20M + lifetime alumni network access.", span: "md:col-span-4", icon: Trophy },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className={`${s.span} group p-8 border border-white/10 bg-black/40 hover:border-[#F26A1F]/60 transition-colors`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-xs uppercase tracking-[0.3em] text-[#1E8E3E] font-bold">
                      Step {s.n}
                    </span>
                    {Icon && <Icon size={20} className="text-[#F26A1F]" />}
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight">{s.t}</h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">{s.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WORKSTREAMS */}
      <section className="border-y border-white/5 bg-[#0c0c0c]">
        <Workstreams compact />
      </section>

      {/* FEATURED CONTESTANTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-28">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#F26A1F] mb-3">
              Meet the Champions
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold tracking-tighter"
              style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
            >
              37 states. 37 dreams.
            </h2>
          </div>
          <Link
            to="/contestants"
            className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 hover:text-[#F26A1F]"
          >
            View all contestants →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {contestants.map((c) => (
            <Link
              key={c.id}
              to={`/contestants?state=${encodeURIComponent(c.state)}`}
              className="group relative aspect-[3/4] overflow-hidden border border-white/10 hover:border-[#F26A1F]/60 transition-colors bg-black"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_farm-show-hub/artifacts/e3nn2b3x_WhatsApp%20Image%202026-05-30%20at%2012.21.52%20AM.jpeg"
                alt="Agrovest contestant placeholder"
                className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-85 group-hover:scale-105 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#F26A1F] font-bold">
                  {c.state}
                </div>
                <div className="text-sm font-bold mt-1 text-white/70">Pending audition</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SPONSORS */}
      <section className="bg-white text-black py-12 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-[10px] uppercase tracking-[0.4em] text-black/60 mb-6 text-center">
            Season 1 Official Partners
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center">
            {SPONSOR_LOGOS.map((s) => (
              <div
                key={s.name}
                className="flex flex-col items-center gap-3 group"
              >
                <div className="h-24 w-24 flex items-center justify-center">
                  <img
                    src={s.logo}
                    alt={s.name}
                    loading="lazy"
                    className="max-h-24 max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="text-center font-bold tracking-tight text-sm md:text-base text-black/80">
                  {s.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0c0c0c] via-[#0A0A0A] to-[#0c0c0c]" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[#1E8E3E] mb-5">
            Your state. Your shot.
          </div>
          <h2
            className="text-4xl md:text-6xl font-black tracking-tighter leading-[1] mb-8"
            style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
          >
            Nigeria is watching.<br />
            <span className="text-[#F26A1F]">Are you ready?</span>
          </h2>
          <p className="text-base text-white/60 max-w-2xl mx-auto mb-10">
            Registration closes soon. One audition can change your entire life.
          </p>
          <Link
            to="/apply"
            className="h-14 px-10 inline-flex items-center gap-3 rounded-sm bg-[#F26A1F] hover:bg-[#D95A15] text-white text-sm uppercase tracking-[0.18em] font-bold transition-all shadow-[0_0_40px_2px_rgba(242,106,31,0.35)]"
          >
            Apply Now, It's Free <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
